import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import MarketplaceBot from "./components/MarketplaceBot";
import AdminDashboard from "./components/AdminDashboard";
import { WalkthroughProvider } from "./walkthrough";
import "./services/axiosAgentInterceptor";
import ProtectedAgent from "./components/security/ProtectedAgent";


// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Marketplace from "./pages/Marketplace";
import AgentDetail from "./pages/AgentDetail";
import VoiceAgentDetail from "./pages/VoiceAgentDetail";
import PrecorChat from "./pages/PrecorChat";
import Dashboard from "./pages/Dashboard";
import PaymentSuccess from "./pages/PaymentSuccess";
import AgentBuilder from "./pages/AgentBuilder";
import KnowledgeBase from "./pages/KnowledgeBase";
import AIAdoptionScorecard from "./pages/AIAdoptionScorecard";
import { PaymentPortal } from "./features/payment";
import AIAdoptionScorecardTool from "./pages/AIAdoptionScorecard";
import ScorecardAdmin from "./pages/ScorecardAdmin";
import AdminRoot from "./pages/admin/AdminRoot";
import PropertyAIApp from "./propertyai/App";
import InteriorDesignAIApp from "./interiorDesignAI/App";
import CodeEngineer from "./pages/CodeEngineer";
import InviteAccept from "./pages/InviteAccept";
import WorkflowList from "./pages/WorkflowList";
import WorkflowEditor from "./pages/WorkflowEditor";
import VisionAgent from "./pages/VisionAgent";



// Plans page is locked — opens contact page in a new tab
const PricingRedirect = () => {
  useEffect(() => {
    window.open("https://netsmartz.com/contact-us/", "_blank", "noopener,noreferrer");
    window.history.back();
  }, []);
  return null;
};


const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F87217]"></div>
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const RoleProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { isAuthenticated, loading, hasAnyRole } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F87217]"></div>
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (requiredRoles.length > 0 && !hasAnyRole(...requiredRoles)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h1>
          <p className="text-gray-600">You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <div className="App" style={{ overflowX: 'hidden' }}>
          <BrowserRouter>
            <WalkthroughProvider>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/agent/:agentId" element={<AgentDetail />} />
              <Route path="/voice-agent/:agentId" element={<VoiceAgentDetail />} />
              <Route path="/precor-chat" element={<PrecorChat />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/pricing" element={<PricingRedirect />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment" element={<ProtectedRoute><PaymentPortal /></ProtectedRoute>} />
              <Route path="/builder" element={<ProtectedRoute><AgentBuilder /></ProtectedRoute>} />
              <Route path="/builder/:agentId" element={<ProtectedRoute><AgentBuilder /></ProtectedRoute>} />
              <Route path="/knowledge-base" element={<ProtectedRoute><KnowledgeBase /></ProtectedRoute>} />
              <Route path="/property-ai/*" element={<PropertyAIApp />} />
              <Route path="/interior-design-ai/*" element={<InteriorDesignAIApp />} />
              <Route path="/ai-readiness" element={<AIAdoptionScorecardTool />} />
              <Route path="/scorecard-admin" element={<RoleProtectedRoute requiredRoles={['admin', 'superadmin']}><ScorecardAdmin /></RoleProtectedRoute>} />
              <Route path="/admin" element={<RoleProtectedRoute requiredRoles={['admin','superadmin']}><AdminRoot /></RoleProtectedRoute>} />
              <Route path="/code-engineer" element={<ProtectedRoute><CodeEngineer /></ProtectedRoute>} />
              <Route path="/opensearchaudit" element={<AdminDashboard />} />
              <Route path="/invite/accept" element={<InviteAccept />} />
              <Route path="/workflow" element={<ProtectedRoute><WorkflowList /></ProtectedRoute>} />
              <Route path="/workflow/:id" element={<ProtectedRoute><WorkflowEditor /></ProtectedRoute>} />
              <Route
                path="/vision-agent"
                element={
                  <ProtectedRoute>

                    <ProtectedAgent agent="vision">

                      <VisionAgent />

                    </ProtectedAgent>

                  </ProtectedRoute>
                }
              />

            </Routes>
            <MarketplaceBot />
          </WalkthroughProvider>

          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
